import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { UserCircle, Home, CheckCircle } from 'lucide-react';
import { formatPhoneDisplay, cleanPhone } from '@/utils/phoneUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PrivateLeadSignIn = () => {
  const { agentId } = useParams();
  const [agentData, setAgentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    working_with_agent: '',
    buying_timeline: '',
    budget_range: '',
    preferred_locations: '',
    notes: '',
    consent_communication: false
  });

  useEffect(() => {
    fetchAgentData();
  }, [agentId]);

  const fetchAgentData = async () => {
    try {
      const response = await axios.get(`${API}/public/agent/${agentId}`);
      setAgentData(response.data);
    } catch (error) {
      console.error('Failed to fetch agent data:', error);
      toast.error('Invalid sign-in link');
    } finally {
      setLoading(false);
    }
  };

  const handlePhoneChange = (e) => {
    const input = e.target.value;
    const digits = cleanPhone(input);
    
    // Limit to 10 digits
    if (digits.length <= 10) {
      const formatted = formatPhoneDisplay(input);
      setFormData({ ...formData, phone: formatted });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);

    try {
      // Clean phone number before sending (remove formatting)
      const submitData = {
        ...formData,
        phone: cleanPhone(formData.phone)
      };
      
      await axios.post(`${API}/private-leads/${agentId}/signin`, submitData);
      
      setSubmitted(true);
      toast.success('Thank you for your information!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit. Please try again.');
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center p-4">
        <p className="text-stone-500">Loading...</p>
      </div>
    );
  }

  if (!agentData) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center p-4">
        <Card className="p-8 max-w-md w-full text-center">
          <Home size={48} className="mx-auto text-stone-400 mb-4" />
          <h2 className="text-2xl font-bold text-stone-800 mb-2">Invalid Link</h2>
          <p className="text-stone-600">This sign-in link is not valid.</p>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center p-4">
        <Card className="p-8 max-w-md w-full text-center">
          <CheckCircle size={64} className="mx-auto text-green-600 mb-4" />
          <h2 className="text-2xl font-bold text-[#1A4D2E] mb-2">Thank You!</h2>
          <p className="text-stone-600 mb-4">
            Your information has been received. {agentData.name} will be in touch with you soon.
          </p>
          <p className="text-sm text-stone-500">
            You can close this page now.
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 flex items-center justify-center p-4">
      <Card className="p-6 md:p-8 max-w-2xl w-full">
        <div className="text-center mb-6">
          <UserCircle size={48} className="mx-auto text-[#1A4D2E] mb-3" />
          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-[#1A4D2E]" style={{fontFamily: '"Playfair Display", serif'}}>
            Connect with {agentData.name}
          </h1>
          <p className="text-stone-600 mt-2">
            Please fill in your information to get started
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="name">Full Name *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              required
              className="mt-1"
            />
          </div>

          <div>
            <Label htmlFor="email">Email *</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              required
              className="mt-1"
            />
          </div>

          <div>
            <Label htmlFor="phone">Phone Number</Label>
            <Input
              id="phone"
              type="tel"
              value={formData.phone}
              onChange={handlePhoneChange}
              placeholder="(555) 123-4567"
              maxLength={14}
              className="mt-1"
            />
            <p className="text-xs text-stone-500 mt-1">Format: (555) 123-4567</p>
          </div>

          <div>
            <Label htmlFor="working_with_agent">Are you currently working with an agent?</Label>
            <select
              id="working_with_agent"
              value={formData.working_with_agent}
              onChange={(e) => setFormData({...formData, working_with_agent: e.target.value})}
              className="w-full mt-1 p-2 border border-stone-300 rounded-md"
            >
              <option value="">Select...</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </div>

          <div>
            <Label htmlFor="buying_timeline">Buying Timeline</Label>
            <select
              id="buying_timeline"
              value={formData.buying_timeline}
              onChange={(e) => setFormData({...formData, buying_timeline: e.target.value})}
              className="w-full mt-1 p-2 border border-stone-300 rounded-md"
            >
              <option value="">Select...</option>
              <option value="immediately">Immediately</option>
              <option value="1-3 months">1-3 months</option>
              <option value="3-6 months">3-6 months</option>
              <option value="6+ months">6+ months</option>
              <option value="just looking">Just looking</option>
            </select>
          </div>

          <div>
            <Label htmlFor="budget_range">Budget Range</Label>
            <select
              id="budget_range"
              value={formData.budget_range}
              onChange={(e) => setFormData({...formData, budget_range: e.target.value})}
              className="w-full mt-1 p-2 border border-stone-300 rounded-md"
            >
              <option value="">Select</option>
              <option value="under-300k">Under $300k</option>
              <option value="300k-500k">$300k - $500k</option>
              <option value="500k-750k">$500k - $750k</option>
              <option value="750k-1m">$750k - $1M</option>
              <option value="1m-2m">$1M - $2M</option>
              <option value="over-2m">Over $2M</option>
            </select>
          </div>

          <div>
            <Label htmlFor="preferred_locations">Preferred Locations</Label>
            <Input
              id="preferred_locations"
              value={formData.preferred_locations}
              onChange={(e) => setFormData({...formData, preferred_locations: e.target.value})}
              placeholder="e.g., Downtown, Suburbs"
              className="mt-1"
            />
          </div>

          <div>
            <Label htmlFor="notes">Additional Notes</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => setFormData({...formData, notes: e.target.value})}
              placeholder="Any additional information you'd like to share..."
              rows={3}
              className="mt-1"
            />
          </div>

          <div className="flex items-start gap-2">
            <Checkbox
              id="consent_communication"
              checked={formData.consent_communication}
              onCheckedChange={(checked) => setFormData({...formData, consent_communication: checked})}
              required
            />
            <Label htmlFor="consent_communication" className="cursor-pointer text-sm">
              I consent to be contacted by {agentData.name} regarding real estate opportunities *
            </Label>
          </div>

          <Button
            type="submit"
            disabled={submitting}
            className="w-full bg-[#1A4D2E] hover:bg-[#143D24] text-white py-6 text-lg"
          >
            {submitting ? 'Submitting...' : 'Submit Information'}
          </Button>
        </form>

        <p className="text-xs text-stone-500 text-center mt-4">
          Your information will be kept private and secure.
        </p>
      </Card>
    </div>
  );
};

export default PrivateLeadSignIn;
